import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Search, ArrowLeft, FileText, Shield, Car, Calendar, 
  Users, ParkingCircle, Stethoscope, Pill, AlertCircle 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import API from '@/utils/api';

/**
 * SearchPage - Global search across all app data
 * 
 * Searches:
 * - Emergency Binder profiles
 * - Documents
 * - Parking locations
 * - Important Dates
 * - Car Maintenance
 * - Medical info
 * - Contacts
 */

const SEARCH_CATEGORIES = {
  'binder': { icon: Shield, color: '#EF4444', label: 'Emergency Binder', path: '/emergency-binder' },
  'document': { icon: FileText, color: '#3B82F6', label: 'Documents', path: '/documents' },
  'parking': { icon: ParkingCircle, color: '#8B5CF6', label: 'Parking', path: '/parking' },
  'date': { icon: Calendar, color: '#EC4899', label: 'Important Date', path: '/dates' },
  'car': { icon: Car, color: '#22C55E', label: 'Car Maintenance', path: '/car-maintenance' },
  'medical': { icon: Stethoscope, color: '#22C55E', label: 'Medical', path: '/medical' },
  'contact': { icon: Users, color: '#8B5CF6', label: 'Contact', path: '/contacts-places' },
  'medication': { icon: Pill, color: '#F59E0B', label: 'Medication', path: '/medical' },
};

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const performSearch = useCallback(async (searchQuery) => {
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await API.get(`/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search when query changes (with debounce)
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleResultClick = (result) => {
    const category = SEARCH_CATEGORIES[result.type] || {};
    if (result.path) {
      navigate(result.path);
    } else if (category.path) {
      navigate(category.path);
    }
  };

  const getCategoryInfo = (type) => {
    return SEARCH_CATEGORIES[type] || { 
      icon: AlertCircle, 
      color: '#64748B', 
      label: 'Other' 
    };
  };

  return (
    <div className="pastel-gradient min-h-screen pb-24" data-testid="search-page">
      {/* Header */}
      <header className="safe-area-top px-4 pt-2 pb-4 sticky top-0 z-40 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center touch-target"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search your life..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="h-12 pl-10 pr-4 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 text-base"
              data-testid="search-input"
            />
          </div>
        </div>
      </header>

      {/* Results */}
      <main className="px-4 py-4 space-y-4">
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
            <p className="text-slate-500 mt-2">Searching...</p>
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-slate-700">No results found</h3>
            <p className="text-slate-500 mt-1">Try a different search term</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
            
            {results.map((result, index) => {
              const category = getCategoryInfo(result.type);
              const Icon = category.icon;
              
              return (
                <Card
                  key={`${result.type}-${index}`}
                  className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleResultClick(result)}
                  data-testid={`search-result-${index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${category.color}15` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: category.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-800 truncate">{result.title}</h4>
                        <p className="text-sm text-slate-500 truncate">{result.subtitle || category.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && !searched && (
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-slate-700">Search everything</h3>
            <p className="text-slate-500 mt-1">Documents, contacts, medications, dates...</p>
            
            {/* Recent Searches or Suggestions */}
            <div className="mt-8 text-left">
              <p className="text-sm font-medium text-slate-500 mb-3">Try searching for:</p>
              <div className="flex flex-wrap gap-2">
                {['passport', 'insurance', 'oil change', 'allergy', 'Dr.'].map((term) => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    className="px-3 py-1.5 bg-white rounded-full text-sm text-slate-600 shadow-sm hover:shadow-md transition-shadow"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchPage;
